import {
  CreateLoginTrackerMutation,
  FetchLoginTrackerQuery,
  FetchUserTrackingQuery,
  ForgotPasswordPageQuery,
  LoginPageQuery,
  RegisterPageQuery,
  ResetPasswordMutation,
  UpdateUserMutation,
  UsersQuery,
} from '../../graphql/generated/graphql';
import { MUTATION_RESET_PASSWORD } from '../../graphql/mutations/auth/reset-password';
import { MUTATION_UPDATE_USER } from '../../graphql/mutations/user/update-user';
import { initializeApollo, initializeApolloWithoutToken } from '../lib/apollo-client';
import axios from 'axios';
import requestHandler from '../requestHandler';
import { QUERY_ME } from '@/graphql/queries/global/me';
import { QUERY_FORGOT_PASSWORD_PAGE, QUERY_LOGIN_PAGE, QUERY_REGISTER_PAGE } from '@/graphql/queries/login/login';
import { MUTATION_LOGIN_TRACKER } from '@/graphql/mutations/auth/login-tracker';
import { FETCH_USER_TRACKING, FETCH_USER_TRACKING_RANGE } from '@/graphql/queries/auth/login-tracker';

export async function login({
  email,
  password,
  rememberMe,
}: {
  email: string;
  password: string;
  rememberMe?: boolean;
}) {
  let data, error;

  try {
    const { data: response } = await axios({
      method: 'post',
      url: `${import.meta.env.VITE_API_URL}/api/auth/local`,
      data: { email, password, rememberMe },
    });

    data = response;
  } catch (err) {
    console.error(err);
    // eslint-disable-next-line import/no-named-as-default-member
    if (axios.isAxiosError(err) && err.response) {
      error = err.response?.data?.error;
    }
  }

  return { data, error };
}

export async function register(user: {
  email: string;
  password: string;
  schoolCode: string;
  forename: string;
  surname: string;
}) {
  let data, error;

  try {
    const { data: response } = await axios({
      method: 'post',
      url: `${import.meta.env.VITE_API_URL}/api/auth/register`,
      data: {
        email: user.email,
        password: user.password,
        schoolCode: user.schoolCode,
        device: 'web',
        forename: user.forename,
        surname: user.surname,
      },
    });

    data = response;
  } catch (err) {
    console.error(err);
    // eslint-disable-next-line import/no-named-as-default-member
    if (axios.isAxiosError(err) && err.response) {
      error = err.response?.data?.error;
    }
  }

  return { data, error };
}

export async function updateProfile(id: string, profile: string) {
  const apolloClient = initializeApolloWithoutToken();

  return requestHandler(
    apolloClient.mutate<UpdateUserMutation>({
      mutation: MUTATION_UPDATE_USER,
      variables: {
        id,
        input: {
          profile,
        },
      },
    }),
  );
}

export async function forgotPassword(email: string) {
  let data, error;

  try {
    const { data: response } = await axios({
      method: 'post',
      url: `${import.meta.env.VITE_API_URL}/api/auth/forgot-password`,
      data: { email, device: 'web' },
    });

    data = response;
  } catch (err) {
    console.error(err);
    // eslint-disable-next-line import/no-named-as-default-member
    if (axios.isAxiosError(err) && err.response) {
      error = err.response?.data?.error;
    }
  }

  return { data, error };
}

export async function resetPassword({
  password,
  passwordConfirmation,
  code,
}: {
  password: string;
  passwordConfirmation: string;
  code: string;
}) {
  const apolloClient = initializeApolloWithoutToken();

  return requestHandler(
    apolloClient.mutate<ResetPasswordMutation>({
      mutation: MUTATION_RESET_PASSWORD,
      variables: {
        password,
        passwordConfirmation,
        code,
      },
    }),
  );
}

export async function getAuthenticationUser(id: string) {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<UsersQuery>({
      query: QUERY_ME,
      variables: {
        id,
      },
    }),
  );

  return { data: data?.usersPermissionsUser?.data?.attributes, error };
}

export async function getForgotPasswordPage() {
  const apolloClient = initializeApolloWithoutToken();

  const { data, error } = await requestHandler(
    apolloClient.query<ForgotPasswordPageQuery>({
      query: QUERY_FORGOT_PASSWORD_PAGE,
      fetchPolicy: 'cache-first',
    }),
  );

  return { page: data?.forgotPassword?.data?.attributes, error };
}

export async function getLoginPage() {
  const apolloClient = initializeApolloWithoutToken();

  const { data, error } = await requestHandler(
    apolloClient.query<LoginPageQuery>({
      query: QUERY_LOGIN_PAGE,
      fetchPolicy: 'cache-first',
    }),
  );

  return { page: data?.loginPage?.data?.attributes, error };
}

export async function getRegisterPage() {
  const apolloClient = initializeApolloWithoutToken();

  const { data, error } = await requestHandler(
    apolloClient.query<RegisterPageQuery>({
      query: QUERY_REGISTER_PAGE,
      fetchPolicy: 'cache-first',
    }),
  );

  return { page: data?.registerPage?.data?.attributes, error };
}

export async function updateSchoolCode(id: string, schoolCode: string) {
  const apolloClient = initializeApolloWithoutToken();

  return requestHandler(
    apolloClient.mutate<UpdateUserMutation>({
      mutation: MUTATION_UPDATE_USER,
      variables: {
        id,
        input: {
          schoolCode,
        },
      },
    }),
  );
}

export async function loginTracker(id: string) {
  if (!id) {
    return null;
  }

  try {
    const apolloClient = initializeApollo();

    return requestHandler(
      apolloClient.mutate<CreateLoginTrackerMutation>({
        mutation: MUTATION_LOGIN_TRACKER,
        variables: {
          input: {
            user: id,
          },
        },
      }),
    );
  } catch (error) {
    /* empty */
  }
}

export const getLoginRange = async (startDay: Date, endDay: Date, id: string) => {
  const apolloClient = initializeApollo();

  const inputData = {
    endDay: endDay.toISOString().split('T')[0],
    startDay: startDay.toISOString().split('T')[0],
    userId: id,
  };

  const { data } = await requestHandler(
    apolloClient.query<FetchLoginTrackerQuery>({
      query: FETCH_USER_TRACKING_RANGE,
      variables: {
        ...inputData,
      },
    }),
  );

  return data?.loginTrackers?.data;
};

export const fetchUserTracking = async () => {
  const apolloClient = initializeApollo();

  const { data } = await requestHandler(
    apolloClient.query<FetchUserTrackingQuery>({
      query: FETCH_USER_TRACKING,
      fetchPolicy: 'cache-first',
    }),
  );

  return data?.getUserTrackingData;
};
